import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Divider, Dropdown, Tabs } from 'antd';

import { InstallSection } from '@/pages/CliOut/InstallSection';
import type { DownloadInfo } from '@/pages/Download/hooks/useDownloadInfo';
import { isInternalDomain } from '@/utils/const';
import { showDownloadStartToast, showVersionMismatchToast } from '@/utils/toastHelper';
import { sendDownloadClickEvent } from '@/utils/weblogger';

import styles from './DownloadSection.module.less';
import AppleImg from './assets/apple.svg?react';
import ArrowImg from './assets/arrow.svg?react';
import CheckedImg from './assets/checked.svg?react';
import JetbrainsImg from './assets/jb.svg?react';
import VscodeImg from './assets/vscode.svg?react';
import WindowImg from './assets/windows.svg?react';
import XcodeImg from './assets/xcode.svg?react';

interface DownloadSectionProps {
  downloadInfo: {
    arm?: DownloadInfo;
    intel?: DownloadInfo;
    windows?: DownloadInfo;
  } | null;
  loading: boolean;
  defaultMacType: 'arm' | 'intel';
  userSystem: 'apple' | 'intel' | 'windows' | null;
}

type TabType = 'ide' | 'extension' | 'cli';

/**
 * DownloadSection 组件
 * 根据设计稿重新实现的下载区域组件
 */
const DownloadSection: React.FC<DownloadSectionProps> = ({
  downloadInfo,
  loading,
  defaultMacType,
  userSystem,
}) => {
  const [params, setParams] = useSearchParams();

  const defaultActiveTab = (params.get('tab') || 'ide') as TabType;

  const { t } = useTranslation();
  const [selectedMacType, setSelectedMacType] = useState<'arm' | 'intel'>(defaultMacType);
  const [activeTab, setActiveTabData] = useState<TabType>(defaultActiveTab);
  const [jetbrainsDownloadUrl, setJetbrainsDownloadUrl] = useState('');
  const [xcodeDownloadUrl, setXcodeDownloadUrl] = useState('');
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  );

  const setActiveTab = useCallback(
    (tab: TabType) => {
      setActiveTabData(tab);

      setParams((params) => {
        params.set('tab', tab);
        return params;
      });
    },
    [setActiveTabData, setParams],
  );

  // 监听窗口宽度变化,用于控制Divider的type
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 当默认Mac类型改变时更新选中状态
  useEffect(() => {
    setSelectedMacType(defaultMacType);
  }, [defaultMacType]);

  useEffect(() => {
    const key = 'kwaipilot.platform.plugin_latest_version_config';
    fetch(`https://codeflicker.corp.kuaishou.com/eapi/kwaipilot/plugin/v2/config?key=${key}`)
      .then((res) => res.json() as Promise<{ data: Record<string, { url: string }> }>)
      .then((res) => {
        setJetbrainsDownloadUrl(res.data['kwaipilot-jetbrains']?.url || '');
        setXcodeDownloadUrl(res.data['kwaipilot-xcode']?.url || '');
      })
      .catch(() => {
        setJetbrainsDownloadUrl('');
        setXcodeDownloadUrl('');
      });
  }, []);

  // 通用下载函数 - 创建临时链接触发下载
  const triggerDownload = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 处理下载
  const handleDownload = (type: 'arm' | 'intel' | 'windows') => {
    if (!downloadInfo) return;

    let downloadUrl = '';
    let version = '';
    let chipType = '';
    let platformName = '';

    if (type === 'arm' && downloadInfo.arm?.url) {
      downloadUrl = downloadInfo.arm.url;
      version = downloadInfo.arm.version;
      chipType = t('download.architectures.appleChip');
      platformName = 'apple';
    } else if (type === 'intel' && downloadInfo.intel?.url) {
      downloadUrl = downloadInfo.intel.url;
      version = downloadInfo.intel.version;
      chipType = t('download.architectures.intelChip');
      platformName = 'intel';
    } else if (type === 'windows' && downloadInfo.windows?.url) {
      downloadUrl = downloadInfo.windows.url;
      version = downloadInfo.windows.version;
      chipType = t('download.platforms.windows');
      platformName = 'windows';
    }

    if (downloadUrl) {
      // 检查版本是否与用户系统匹配
      let isMismatch = false;
      let currentSystemName = '';
      let downloadSystemName = '';

      if (userSystem) {
        // 判断是否匹配
        if (userSystem === 'apple' && type !== 'arm') {
          isMismatch = true;
          currentSystemName = t('download.architectures.appleSilicon');
          downloadSystemName =
            type === 'intel' ? t('download.architectures.intel') : t('download.platforms.windows');
        } else if (userSystem === 'intel' && type !== 'intel') {
          isMismatch = true;
          currentSystemName = t('download.architectures.intel');
          downloadSystemName =
            type === 'arm'
              ? t('download.architectures.appleSilicon')
              : t('download.platforms.windows');
        } else if (userSystem === 'windows' && type !== 'windows') {
          isMismatch = true;
          currentSystemName = t('download.platforms.windows');
          downloadSystemName =
            type === 'arm'
              ? t('download.architectures.appleSilicon')
              : t('download.architectures.intel');
        }

        // 如果版本不匹配，显示警告提醒
        if (isMismatch) {
          showVersionMismatchToast(currentSystemName, downloadSystemName);
        }
      }

      // 埋点上报：Download页面下载按钮点击
      sendDownloadClickEvent(platformName, version || 'unknown', 'download');

      // 显示开始下载的 toast 提示
      showDownloadStartToast(chipType, version);

      // 触发下载
      triggerDownload(downloadUrl);
    }
  };

  return (
    <div className={styles.downloadSection}>
      <div className={styles.contentContainer}>
        {/* 标题区域 */}
        <div className={styles.downloadHeader}>
          <h1 className={styles.downloadTitle}>
            <span className={styles.titleNormal}>{t('download.titlePrefix')}</span>
            <span className={styles.titleHighlight}>{t('download.titleHighlight')}</span>
            {/* <span className={styles.titleNormal}>{t('download.titleSuffix')}</span> */}
          </h1>
          <p className={styles.downloadSubtitle}>{t('download.subtitle')}</p>
        </div>

        {/* Tab 切换区域 */}
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'ide' | 'extension' | 'cli')}
          centered
          className={styles.downloadTabs}
          items={[
            {
              key: 'ide',
              label: t('download.tabs.ide'),
            },
            ...(isInternalDomain()
              ? [
                  {
                    key: 'extension',
                    label: t('download.tabs.extension'),
                  },
                ]
              : []),
            {
              key: 'cli',
              label: t('download.tabs.cli'),
            },
          ]}
        />
        <div className={styles.tabContent}>
          {/* 下载卡片区域 - 根据activeTab显示 */}
          {activeTab === 'ide' && (
            <div className={styles.ideTabContent}>
              {/* IDE Tab 标题 */}
              <div className={styles.ideTabTitle}>
                <h2 className={styles.ideMainTitle}>{t('download.ide.title')}</h2>
                <p className={styles.ideSubtitle}>{t('download.ide.subtitle')}</p>
              </div>

              {/* 下载卡片容器 */}
              <div className={styles.downloadCardsContainer}>
                {/* macOS 卡片 */}
                <div className={`${styles.downloadCard} ${styles.macosCard}`}>
                  {/* 卡片内容 */}
                  <div className={styles.cardContentWrapper}>
                    {/* 平台信息 */}
                    <div className={styles.platformSection}>
                      <div className={styles.platformIconWrapper}>
                        <AppleImg />
                      </div>
                      <div className={styles.platformInfo}>
                        <h3 className={styles.platformTitle}>{t('download.platforms.macos')}</h3>
                        <p className={styles.platformRequirement}>
                          {t('download.platforms.requirement')}
                        </p>
                      </div>
                    </div>

                    {/* 下载按钮组 */}
                    <Dropdown
                      placement="bottomRight"
                      trigger={['click']}
                      disabled={loading}
                      overlayClassName={styles.macDropdownMenu}
                      autoAdjustOverflow
                      menu={{
                        className: styles.macDropdownMenuInner,
                        selectedKeys: [selectedMacType],
                        items: [
                          {
                            key: 'arm',
                            label: (
                              <div className={styles.dropdownItemLabel}>
                                <span>{t('download.buttons.downloadForAppleSilicon')}</span>
                                {selectedMacType === 'arm' && <CheckedImg />}
                              </div>
                            ),
                            onClick: () => {
                              setSelectedMacType('arm');
                              handleDownload('arm');
                            },
                            disabled: loading || !downloadInfo?.arm?.url,
                          },
                          {
                            key: 'intel',
                            label: (
                              <div className={styles.dropdownItemLabel}>
                                <span>{t('download.buttons.downloadForIntel')}</span>
                                {selectedMacType === 'intel' && <CheckedImg />}
                              </div>
                            ),
                            onClick: () => {
                              setSelectedMacType('intel');
                              handleDownload('intel');
                            },
                            disabled: loading || !downloadInfo?.intel?.url,
                          },
                        ],
                      }}
                    >
                      <button
                        className={`${styles.downloadButton} ${styles.macos} ${styles.unifiedButton}`}
                        disabled={loading}
                      >
                        <span className={styles.buttonText}>
                          {t('download.buttons.downloadFor', {
                            platform:
                              selectedMacType === 'arm'
                                ? t('download.architectures.appleSilicon')
                                : t('download.architectures.intel'),
                          })}
                        </span>
                        <ArrowImg />
                      </button>
                    </Dropdown>
                  </div>
                </div>

                <Divider
                  type={windowWidth > 767 ? 'vertical' : 'horizontal'}
                  className={styles.cardsDivider}
                  style={{ backgroundColor: 'rgba(183, 179, 177, 0.3)' }}
                />

                {/* Windows 卡片 */}
                <div className={`${styles.downloadCard} ${styles.windowsCard}`}>
                  {/* 卡片内容 */}
                  <div className={styles.cardContentWrapper}>
                    {/* 平台信息 */}
                    <div className={styles.platformSection}>
                      <div className={styles.platformIconWrapper}>
                        <WindowImg />
                      </div>
                      <div className={styles.platformInfo}>
                        <h3 className={styles.platformTitle}>{t('download.platforms.windows')}</h3>
                        <p className={styles.platformRequirement}>
                          {t('download.platforms.winRequirement')}
                        </p>
                      </div>
                    </div>

                    {/* Windows 下载按钮 */}
                    <div className={styles.downloadButtonGroup}>
                      <button
                        className={`${styles.downloadButton} ${styles.windows} ${styles.standalone}`}
                        onClick={() => handleDownload('windows')}
                        // disabled={true}
                        disabled={loading || !downloadInfo?.windows?.url}
                      >
                        <span className={styles.buttonText}>
                          {/* {t('download.buttons.comingSoon')} */}
                            {downloadInfo?.windows?.url
                              ? `${t('download.buttons.downloadFor', {
                                  platform: t('download.platforms.windows'),
                                })}`
                              : t('download.buttons.comingSoon')}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Extension tab 内容 */}
          {activeTab === 'extension' && (
            <div className={styles.extensionTabContent}>
              <div className={styles.extensionTabInner}>
                {/* 标题区域 */}
                <div className={styles.extensionTabTitle}>
                  <h2 className={styles.extensionMainTitle}>{t('download.extension.title')}</h2>
                  <p className={styles.extensionSubtitle}>{t('download.extension.subtitle')}</p>
                </div>

                {/* 平台图标区域 */}
                <div className={styles.extensionPlatforms}>
                  {/* VS Code */}
                  <div
                    className={styles.extensionPlatformItem}
                    onClick={() => {
                      window.open(
                        'https://marketplace.visualstudio.com/items?itemName=kuaishou.kwaipilot',
                      );
                    }}
                  >
                    <VscodeImg />
                    <h3 className={styles.extensionPlatformName}>
                      {t('download.extension.platforms.vscode')}
                    </h3>
                  </div>

                  <Divider
                    type={windowWidth > 1023 ? 'vertical' : 'horizontal'}
                    className={styles.extensionDivider}
                    style={{ backgroundColor: 'rgba(183, 179, 177, 0.3)' }}
                  />

                  {/* JetBrains */}
                  <div
                    className={styles.extensionPlatformItem}
                    onClick={() => {
                      window.open(
                        'https://docs.corp.kuaishou.com/d/home/fcADP5CT9GlAVaq4DLJ02Fahg',
                        '_blank',
                      );
                    }}
                  >
                    <JetbrainsImg />
                    <h3 className={styles.extensionPlatformName}>
                      {t('download.extension.platforms.jetbrains')}
                    </h3>
                  </div>

                  <Divider
                    type={windowWidth > 1023 ? 'vertical' : 'horizontal'}
                    className={styles.extensionDivider}
                    style={{ backgroundColor: 'rgba(183, 179, 177, 0.3)' }}
                  />

                  {/* Xcode */}
                  <div
                    className={styles.extensionPlatformItem}
                    onClick={() => {
                      window.open(
                        'https://docs.corp.kuaishou.com/d/home/fcAA_GMKDYWrU-dQl4v8No7Wl',
                        '_blank',
                      );
                    }}
                  >
                    <XcodeImg />
                    <h3 className={styles.extensionPlatformName}>
                      {t('download.extension.platforms.xcode')}
                    </h3>
                  </div>
                </div>

                {/* 下载按钮 */}
                <Dropdown
                  placement="bottomRight"
                  trigger={['click']}
                  overlayClassName={styles.extensionDropdownMenu}
                  autoAdjustOverflow
                  menu={{
                    className: styles.extensionDropdownMenuInner,
                    items: [
                      {
                        key: 'vscode',
                        label: t('download.extension.buttons.installInVSCode'),
                        onClick: () => {
                          window.open(
                            'https://marketplace.visualstudio.com/items?itemName=kuaishou.kwaipilot',
                          );
                        },
                      },
                      {
                        key: 'jetbrains',
                        label: t('download.extension.buttons.installInJetBrains'),
                        onClick: () => {
                          // if (jetbrainsDownloadUrl) {
                          //   triggerDownload(jetbrainsDownloadUrl);
                          // }
                          window.open(
                            'https://docs.corp.kuaishou.com/d/home/fcADP5CT9GlAVaq4DLJ02Fahg',
                            '_blank',
                          );
                        },
                        disabled: !jetbrainsDownloadUrl,
                      },
                      {
                        key: 'xcode',
                        label: t('download.extension.buttons.installInXcode'),
                        onClick: () => {
                          window.open(
                            'https://docs.corp.kuaishou.com/d/home/fcAA_GMKDYWrU-dQl4v8No7Wl',
                            '_blank',
                          );
                        },
                        disabled: !xcodeDownloadUrl,
                      },
                    ],
                  }}
                >
                  <button className={styles.extensionInstallButton}>
                    <span className={styles.extensionInstallButtonText}>
                      {t('download.extension.buttons.installInIDE')}
                    </span>
                    <ArrowImg />
                  </button>
                </Dropdown>
              </div>
            </div>
          )}

          {/* CLI tab 内容 */}
          {activeTab === 'cli' && (
            <div className={styles.clitabContent}>
              <div style={{ border: '1px solid #b7b3b1', borderRadius: '4px' }}>
                <InstallSection />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DownloadSection;
